import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import {formatDate} from '../utils/dateTime';
import {observer} from 'mobx-react-lite';
import {useWardrobe} from '../context/WardrobeContext';
import Spinner from '../components/Spinner';
import {
    DeclutterAction,
    DeclutterCategoryGoal,
    DeclutterItemCard,
    DeclutterProgress,
    DeclutterQueueItem,
    DeclutterReassessment,
    DeclutterSession,
    RestorationKind,
    fetchOverview,
} from '../service/DeclutterApi';
import declutterStore from '../store/DeclutterStore';
import GracePeriodSlider, {GRACE_FALLBACK_MAX} from '../components/declutter/GracePeriodSlider';
import DeclutterDuelCard from '../components/DeclutterDuelCard';
import DeclutterObjections from '../components/DeclutterObjections';
import DeclutterPhysicalStep from '../components/DeclutterPhysicalStep';
import DeclutterReportCard from '../components/DeclutterReportCard';
import SaleListings from '../components/SaleListings';
import AuthImage from '../components/AuthImage';
import {
    LADDER_ACTIONS,
    MODE_OPTIONS,
    PRIMARY_ACTIONS,
    actionLabel,
    bucketLabel,
    lastWornLabel,
    progressLabel,
    sessionStatusLabel,
} from '../support/declutterLabels';
import {pluralItems} from '../utils/plural';
import {useToast} from '../context/ToastContext';

/**
 * Разбор гардероба по стадиям: обзор → решения (открытая сессия) → после решений (отчёт,
 * физический шаг, объявления — пока есть что доводить до конца) → история.
 *
 * Стадия выводится из статуса последней сессии, а не из «есть ли открытая»: после «Завершить
 * решения» открытой сессии нет, но именно тогда и нужны отчёт с физическим шагом.
 *
 * Экран намеренно не показывает проценты: прогресс всегда «N из M выбрано на выбытие».
 * «Итоги решений» — это ещё не «Итоги разбора»: пока вещи лежат в пакете у шкафа, место не
 * освобождено, поэтому объём и вес здесь не заявляются.
 */

/** Решения, которые оставляют вещь в гардеробе, меняя её назначение. */
const REASSIGN_ACTIONS: DeclutterAction[] = ['MOVE_TO_HOME', 'MOVE_TO_WORK'];

function ProgressBar({progress}: {progress: DeclutterProgress}) {
    const reassigned = REASSIGN_ACTIONS.reduce((sum, a) => sum + (progress.breakdown[a] ?? 0), 0);
    const pct = progress.targetCount > 0
        ? Math.min(100, Math.round((progress.decidedForRemoval / progress.targetCount) * 100))
        : 0;
    return (
        <div data-testid="declutter-progress">
            <div className="flex items-baseline justify-between mb-2">
                <span className="text-sm font-medium text-ink-900 dark:text-ink-100">
                    {progressLabel(progress.decidedForRemoval, progress.targetCount)}
                </span>
                {progress.targetReached && (
                    <span className="badge badge-success" data-testid="declutter-target-reached">
                        Цель достигнута
                    </span>
                )}
            </div>
            <div
                className="h-2 rounded-full bg-ink-100 dark:bg-ink-700 overflow-hidden"
                role="progressbar"
                aria-valuenow={progress.decidedForRemoval}
                aria-valuemin={0}
                aria-valuemax={progress.targetCount}
            >
                <div className="h-full rounded-full bg-brand-500" style={{width: `${pct}%`}}/>
            </div>
            <div className="mt-2 flex flex-wrap gap-2 text-xs text-ink-500 dark:text-ink-400">
                {Object.entries(progress.breakdown)
                    .filter(([action]) => !REASSIGN_ACTIONS.includes(action as DeclutterAction))
                    .map(([action, count]) => (
                        <span key={action} className="chip">{actionLabel(action)}: {count}</span>
                    ))}
                {progress.keptCount > 0 && <span className="chip">Оставлено: {progress.keptCount}</span>}
                {reassigned > 0 && (
                    /* Q13: «Донашивать дома» — не расставание, и в «выбрано на выбытие» ему не место. */
                    <span className="chip" data-testid="declutter-reassigned"
                          title="Вещь осталась в гардеробе, но сменила назначение — домашняя или рабочая">
                        Переназначено: {reassigned}
                    </span>
                )}
                {progress.deferredCount > 0 && (
                    <span className="chip">Отложено: {progress.deferredCount}</span>
                )}
            </div>
        </div>
    );
}

function FactorRow({label, impact, known}: {label: string; impact: number; known: boolean}) {
    const positive = impact > 0;
    const width = Math.min(100, Math.abs(impact) * 3);
    return (
        <li className="flex items-center gap-3 py-1.5" data-testid="declutter-factor">
            <span className={`w-10 shrink-0 text-sm font-medium tabular-nums ${
                known ? (positive ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400')
                    : 'text-ink-400'}`}>
                {known ? `${positive ? '+' : ''}${impact}` : '—'}
            </span>
            <span className="flex-1 text-sm text-ink-700 dark:text-ink-300">{label}</span>
            <span className="hidden sm:block w-28 h-1.5 rounded-full bg-ink-100 dark:bg-ink-700 overflow-hidden">
                <span
                    className={`block h-full rounded-full ${positive ? 'bg-emerald-500' : 'bg-red-500'}`}
                    style={{width: `${known ? width : 0}%`}}
                />
            </span>
        </li>
    );
}

/**
 * Служебные факторы разложения оценки, которые пользователю показывать незачем.
 * `FACTOR_SCALE_LIMIT` («Ограничение шкалы оценки») — техническая поправка формулы,
 * а не довод за или против вещи (Q13).
 */
const HIDDEN_FACTOR_CODES = ['FACTOR_SCALE_LIMIT', 'SCALE_LIMIT'];

/**
 * Краткое решение по вещи (F12). Сверху — одно предложение и одно действие; всё численное
 * разложение уезжает под раскрытие. Раньше карточка начиналась со столбика слагаемых, и
 * «+11 Ограничение шкалы» визуально конкурировало с сутью рекомендации.
 *
 * Поля `summaryReasons` и `primaryAction` приходят из бэкенда как необязательные: пока их нет,
 * краткое объяснение собирается из того же, что уже есть — вердикта и первых двух причин.
 */
function summaryOf(card: DeclutterItemCard): {action: DeclutterAction | null; reasons: string[]} {
    // Backend отдаёт в primaryAction подпись («Утилизировать»), а не код: предвыбор варианта
    // возможен только по коду, иначе совет есть, а выбранного варианта нет.
    const action = isDeclutterAction(card.primaryAction) ? card.primaryAction : card.recommendedAction;
    const given = card.summaryReasons?.filter(Boolean) ?? [];
    if (given.length > 0) {
        return {action, reasons: given.slice(0, 3)};
    }
    const fromReasons = card.reasons.slice(0, 2);
    if (fromReasons.length > 0) {
        return {action, reasons: fromReasons};
    }
    // Ни причин, ни краткой сводки: берём два самых весомых известных фактора — они хотя бы
    // названы словами, в отличие от голого балла.
    const fromFactors = card.factors
        .filter(f => f.known && !HIDDEN_FACTOR_CODES.includes(f.code))
        .sort((a, b) => Math.abs(b.impact) - Math.abs(a.impact))
        .slice(0, 2)
        .map(f => f.label);
    return {action, reasons: fromFactors};
}

/** Все решения одним списком: расставание, «оставить» и переназначение — варианты одного выбора. */
const ALL_DECISIONS: DeclutterAction[] = [...PRIMARY_ACTIONS, ...LADDER_ACTIONS];

function isDeclutterAction(value: unknown): value is DeclutterAction {
    return typeof value === 'string' && (ALL_DECISIONS as string[]).includes(value);
}

/** Подпись совета: текст backend (в серой зоне — «Проверить»), иначе подпись кода. */
function adviceLabel(card: DeclutterItemCard, advised: DeclutterAction | null): string | null {
    if (typeof card.primaryAction === 'string' && !isDeclutterAction(card.primaryAction)) {
        return card.primaryAction;
    }
    return advised ? actionLabel(advised) : null;
}

/**
 * Область решения (D09). Раньше совет был отдельной крупной кнопкой, и то же самое действие
 * стояло второй раз в общем ряду: одно решение выглядело двумя разными. Теперь совет
 * <b>предвыбирает</b> вариант, а нажать можно одну кнопку — «Применить решение». Так на область
 * приходится ровно одна primary-кнопка, и видно, что варианты равноправны.
 *
 * <p>Устаревшая оценка (D03) не прячет варианты, а запирает применение: выбрать можно, но пока
 * расчёт не обновлён либо пользователь явно не согласился решать по текущим данным, решение не
 * уходит. Иначе уверенный совет по устаревшим данным исполнялся бы одним нажатием.</p>
 */
function DecisionRegion({card, onDecide, onDecideCategory, deciding, blocked}: {
    card: DeclutterItemCard;
    onDecide: (action: DeclutterAction) => void;
    /** То же решение для всех нерешённых вещей категории текущей вещи. */
    onDecideCategory?: (action: DeclutterAction) => void;
    deciding: boolean;
    /** Оценка устарела, и решение по ней не принимается, пока это не разрешено явно (D03). */
    blocked?: boolean;
}) {
    const {action: advised, reasons} = summaryOf(card);
    const [choice, setChoice] = useState<DeclutterAction | null>(card.decidedAction ?? advised);
    const [categoryArmed, setCategoryArmed] = useState(false);
    const locked = deciding || !!blocked;

    // Следующая вещь очереди приходит со своим советом: выбор не должен переезжать с прошлой.
    useEffect(() => {
        setChoice(card.decidedAction ?? advised);
        setCategoryArmed(false);
    }, [card.itemId, card.decidedAction, advised]);

    return (
        <div className="card p-4" data-testid="declutter-verdict"
             aria-describedby={blocked ? 'declutter-outdated-note' : undefined}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
                <h3 className="text-sm font-medium text-ink-900 dark:text-ink-100">Ваше решение</h3>
                {adviceLabel(card, advised) && (
                    <span className="text-xs text-ink-500 dark:text-ink-400"
                          data-testid="declutter-recommendation">
                        Совет: {adviceLabel(card, advised)}
                    </span>
                )}
            </div>
            {reasons.length > 0 && (
                <ul className="mt-2 list-disc pl-5 text-sm text-ink-600 dark:text-ink-300"
                    data-testid="declutter-summary-reasons">
                    {reasons.map(r => <li key={r}>{r}</li>)}
                </ul>
            )}
            {card.decidedAction && (
                <p className="mt-2 text-sm text-emerald-700 dark:text-emerald-400"
                   data-testid="declutter-decision">
                    Ваше решение: {actionLabel(card.decidedAction)}
                </p>
            )}
            {blocked && (
                <p className="mt-2 text-sm text-amber-700 dark:text-amber-400"
                   id="declutter-outdated-note" data-testid="declutter-outdated-note">
                    Решение заблокировано: вещь изменилась после расчёта. Обновите оценку —
                    или подтвердите, что решаете по текущим данным.
                </p>
            )}

            <div role="radiogroup" aria-label="Решение по вещи" className="mt-3 grid gap-1 sm:grid-cols-2"
                 data-testid="declutter-options">
                {ALL_DECISIONS.map(a => (
                    <label key={a}
                           className={`flex items-center gap-3 min-h-[44px] px-3 rounded-xl cursor-pointer border
                                       transition-colors duration-ui ease-out ${
                               choice === a
                                   ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10'
                                   : 'border-transparent hover:border-brand-500'
                           }`}>
                        <input type="radio" name={`declutter-decision-${card.itemId}`} value={a}
                               checked={choice === a} disabled={deciding}
                               onChange={() => setChoice(a)}
                               data-testid={`declutter-action-${a}`}
                               className="w-5 h-5 shrink-0 accent-brand-600"/>
                        <span className="text-sm text-ink-800 dark:text-ink-200">{actionLabel(a)}</span>
                        {a === advised && (
                            <span className="chip ml-auto text-xs" data-testid="declutter-advised-badge">Совет</span>
                        )}
                    </label>
                ))}
            </div>
            <p className="mt-1 text-xs text-ink-500 dark:text-ink-400">
                «Донашивать дома» и «Перевести в рабочие» — вещь остаётся в гардеробе, меняется её назначение.
            </p>

            <div className="mt-3 flex flex-wrap items-center gap-3">
                {/* Одна primary-кнопка на область (D09); устаревшая оценка запирает именно её (D03). */}
                <button type="button" disabled={locked || !choice}
                        onClick={() => choice && onDecide(choice)}
                        data-testid="declutter-apply"
                        className="btn-accent min-h-[44px] px-5 rounded-xl text-sm font-medium
                                   focus-visible:ring-2 focus-visible:ring-brand-500 disabled:opacity-50">
                    {deciding ? 'Сохраняем…' : 'Применить решение'}
                </button>
                {/* Совет исполняется одним нажатием, даже если в списке выбрано другое. */}
                {advised && !card.decidedAction && (
                    <button type="button" disabled={locked}
                            onClick={() => onDecide(advised)}
                            data-testid="declutter-apply-advised"
                            className="btn-secondary min-h-[44px] px-4 rounded-xl text-sm">
                        Исполнить совет: {actionLabel(advised)}
                    </button>
                )}
                {/* D12: «Подтверждение необратимо» стояло там, где возврат ещё возможен. */}
                <span className="text-xs text-ink-500 dark:text-ink-400" data-testid="declutter-reversible">
                    Решение обратимо, пока вещь у вас.
                </span>
            </div>
            {onDecideCategory && card.categoryName && (
                <div className="mt-3 flex flex-wrap items-center gap-3" data-testid="declutter-category-block">
                    {categoryArmed ? (
                        <>
                            <span className="text-sm text-ink-700 dark:text-ink-300">
                                Применить «{choice ? actionLabel(choice) : '—'}» ко всем нерешённым вещам
                                категории «{card.categoryName}»?
                            </span>
                            <button type="button" disabled={locked || !choice}
                                    onClick={() => choice && onDecideCategory(choice)}
                                    data-testid="declutter-apply-category-confirm"
                                    className="btn-secondary min-h-[44px] px-4 rounded-xl text-sm">
                                Да, ко всей категории
                            </button>
                            <button type="button" onClick={() => setCategoryArmed(false)}
                                    data-testid="declutter-apply-category-cancel"
                                    className="btn-secondary min-h-[44px] px-3 rounded-xl text-sm">
                                Отмена
                            </button>
                        </>
                    ) : (
                        <button type="button" disabled={locked || !choice}
                                onClick={() => setCategoryArmed(true)}
                                data-testid="declutter-apply-category"
                                className="btn-secondary min-h-[44px] px-3 rounded-xl text-sm">
                            Так же для всей категории «{card.categoryName}»
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

/** Виды попытки в ветке «Сначала попробовать» (D10) — подписи те же, что на backend. */
const RESTORATION_KINDS: {value: RestorationKind; label: string}[] = [
    {value: 'REPAIR', label: 'Починить'},
    {value: 'CLEAN', label: 'Почистить'},
    {value: 'ALTER', label: 'Переделать'},
    {value: 'TRIAL', label: 'Проверить в носке'},
];

/**
 * «Сначала попробовать» (аудит 17.09, D10).
 *
 * <p>Между «оставить» и «расстаться» ничего не было, и дефектная вещь ехала к утилизации,
 * ни разу не побывав в мастерской. Здесь решение переносится на дату: вещь остаётся в
 * гардеробе, но из обычной носки и из очереди выбытия выводится.</p>
 */
/** Срок ветки «Сначала попробовать» по умолчанию: месяц на ремонт, чистку или пробную носку. */
const TRY_FIRST_DEFAULT_DAYS = 30;

/** Дата через N дней в формате поля даты (локальный календарь, без сдвига в UTC). */
function isoDateAfterDays(days: number): string {
    const d = new Date();
    d.setDate(d.getDate() + days);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function TryFirstPanel({onTry, busy}: {
    onTry: (kind: RestorationKind, dueDate: string | null) => void;
    busy: boolean;
}) {
    const [kind, setKind] = useState<RestorationKind>('REPAIR');
    const [dueDate, setDueDate] = useState(() => isoDateAfterDays(TRY_FIRST_DEFAULT_DAYS));
    return (
        <div className="card p-4" data-testid="declutter-try-first">
            <h4 className="text-sm font-medium text-ink-900 dark:text-ink-100">
                Сначала попробовать
            </h4>
            <p className="mt-1 text-sm text-ink-500 dark:text-ink-400">
                Вещь останется в гардеробе, но выйдет из подборов и очереди выбытия до этой даты.
            </p>
            <fieldset className="mt-3 min-w-0">
                <legend className="mb-1 text-sm text-ink-700 dark:text-ink-300">Что сделаете</legend>
                {/* Тот же ряд вариантов, что и у решения по вещи — не системный select. */}
                <div role="radiogroup" aria-label="Что сделаете" data-testid="declutter-try-first-kind"
                     className="flex flex-wrap gap-1">
                    {RESTORATION_KINDS.map(k => (
                        <label key={k.value}
                               className={`inline-flex items-center gap-2 min-h-[44px] px-3 rounded-xl cursor-pointer
                                           border transition-colors duration-ui ease-out ${
                                   kind === k.value
                                       ? 'border-brand-500 bg-brand-50 dark:bg-brand-500/10'
                                       : 'border-transparent hover:border-brand-500'
                               }`}>
                            <input type="radio" name="declutter-try-first-kind" value={k.value}
                                   checked={kind === k.value} disabled={busy}
                                   onChange={() => setKind(k.value)}
                                   data-testid={`declutter-try-first-kind-${k.value}`}
                                   className="w-4 h-4 shrink-0 accent-brand-600"/>
                            <span className="text-sm text-ink-800 dark:text-ink-200">{k.label}</span>
                        </label>
                    ))}
                </div>
            </fieldset>
            <div className="mt-3 flex flex-wrap items-end gap-3">
                <label className="text-sm text-ink-700 dark:text-ink-300">
                    <span className="block mb-1">Вернуться к решению</span>
                    <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)}
                           data-testid="declutter-try-first-due"
                           className="input min-h-[44px] rounded-xl"/>
                </label>
                <button type="button" disabled={busy} data-testid="declutter-try-first-submit"
                        onClick={() => onTry(kind, dueDate || null)}
                        className="btn-secondary min-h-[44px] px-4 rounded-xl text-sm">
                    Отправить на восстановление
                </button>
            </div>
        </div>
    );
}

/** Дифф оценки «было → стало» после пересчёта (D03). */
function ReassessmentDiff({result}: {result: DeclutterReassessment}) {
    if (!result.changed) {
        return (
            <p className="mt-2 text-xs text-ink-500 dark:text-ink-400"
               data-testid="declutter-reassess-same">
                Пересчитано {formatDate(result.computedAt)} — оценка не изменилась.
            </p>
        );
    }
    return (
        <div className="mt-2 text-xs text-ink-600 dark:text-ink-300"
             data-testid="declutter-reassess-diff">
            <p>Пересчитано {formatDate(result.computedAt)}:</p>
            <ul className="mt-1 list-disc pl-5 space-y-0.5">
                <li>
                    Балл: {result.before.score ?? '—'} → {result.after.score ?? '—'}
                </li>
                <li>
                    Совет: {result.before.actionLabel ?? '—'} → {result.after.actionLabel ?? '—'}
                </li>
                {result.after.summaryReasons.length > 0 && (
                    <li>Причины теперь: {result.after.summaryReasons.join('; ')}</li>
                )}
            </ul>
        </div>
    );
}

function ItemCardView(props: {
    card: DeclutterItemCard;
    onDecide: (action: DeclutterAction) => void;
    onDecideCategory?: (action: DeclutterAction) => void;
    deciding: boolean;
    ownerId: string | null;
    sessionId: string;
    cardRef?: React.Ref<HTMLDivElement>;
    onRefreshScore?: () => void;
    refreshingScore?: boolean;
    /** Пользователь явно согласился решать по текущим данным (D03). */
    acceptOutdated?: boolean;
    onAcceptOutdated?: () => void;
    reassessment?: DeclutterReassessment | null;
    reassessError?: string | null;
    onTryFirst?: (kind: RestorationKind, dueDate: string | null) => void;
}) {
    const {card, onDecide, deciding, ownerId, sessionId, cardRef, onRefreshScore,
        refreshingScore, acceptOutdated, onAcceptOutdated, reassessment, reassessError,
        onTryFirst} = props;
    const visibleFactors = card.factors.filter(f => !HIDDEN_FACTOR_CODES.includes(f.code));
    const blocked = card.stale && !acceptOutdated;
    return (
        /* D09: порядок блоков — сначала «что это за вещь и насколько свежие данные», потом
           доказательство, и только потом решение. Раньше действия шли до фото и названия, и
           человек нажимал кнопку, не успев узнать вещь. */
        <div className="grid gap-4 lg:grid-cols-2" data-testid="declutter-card" ref={cardRef}
             tabIndex={-1} style={{scrollMarginTop: '5rem'}}>
            <div className="card p-4" data-testid="declutter-identity">
                <div className="flex gap-4">
                    {card.primaryPhotoUrl ? (
                        <AuthImage imageKey={card.primaryPhotoUrl} alt={card.name} variant="medium"
                                   className="w-28 h-36 object-cover rounded-xl"/>
                    ) : (
                        <div className="w-28 h-36 rounded-xl bg-ink-100 dark:bg-ink-700"/>
                    )}
                    <div className="min-w-0">
                        <h3 className="text-lg font-semibold text-ink-900 dark:text-ink-100 truncate">
                            {card.name}
                        </h3>
                        {card.brandName && (
                            <p className="text-sm text-ink-500 dark:text-ink-400">{card.brandName}</p>
                        )}
                        <dl className="mt-3 space-y-1 text-sm text-ink-600 dark:text-ink-300">
                            {card.categoryName && <div>Категория: {card.categoryName}</div>}
                            {card.size && <div>Размер: {card.size}</div>}
                            {/* D04: «Состояние» — только физическое; интегральный балл
                                называется «Полезность для гардероба» и живёт в деталях. */}
                            {card.conditionName && (
                                <div data-testid="declutter-condition">
                                    Физическое состояние: {card.conditionName}
                                </div>
                            )}
                            {card.physicalCondition && (
                                <div data-testid="declutter-repairability">
                                    Повреждения: {card.physicalCondition.summary}
                                </div>
                            )}
                            <div>Носок: {card.totalWears ?? 0}</div>
                            <div>Последняя носка: {lastWornLabel(card.lastWornAt)}</div>
                            {card.priceRub && <div>Цена покупки: {card.priceRub} ₽</div>}
                        </dl>
                    </div>
                </div>
                {card.stale && (
                    <div className="mt-3" data-testid="declutter-stale">
                        <p className="text-xs text-amber-600 dark:text-amber-400">
                            Вещь изменилась после расчёта оценки
                            {card.assessmentComputedAt
                                ? ` (расчёт от ${formatDate(card.assessmentComputedAt)})`
                                : ''}. Решение по устаревшему совету не принимается.
                        </p>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                            {onRefreshScore && (
                                <button type="button"
                                        className="btn-secondary min-h-[44px] px-3 rounded-xl text-xs"
                                        data-testid="declutter-refresh-score"
                                        disabled={refreshingScore} aria-busy={refreshingScore}
                                        onClick={onRefreshScore}>
                                    {refreshingScore ? 'Обновляем…' : 'Обновить оценку'}
                                </button>
                            )}
                            {onAcceptOutdated && !acceptOutdated && (
                                <button type="button"
                                        className="min-h-[44px] px-3 rounded-xl text-xs text-brand-600
                                                   dark:text-brand-400 hover:underline"
                                        data-testid="declutter-accept-outdated"
                                        onClick={onAcceptOutdated}>
                                    Решить по текущим данным
                                </button>
                            )}
                        </div>
                        {reassessError && (
                            <p className="mt-2 text-xs text-red-600 dark:text-red-400" role="alert"
                               data-testid="declutter-reassess-error">
                                {reassessError}
                            </p>
                        )}
                    </div>
                )}
                {reassessment && !reassessError && <ReassessmentDiff result={reassessment}/>}
                <div className="mt-3 flex flex-wrap gap-3 text-sm">
                    <Link to={`/clothing/${card.itemId}`}
                          className="text-brand-600 dark:text-brand-400 hover:underline inline-flex items-center min-h-[44px]">
                        Открыть карточку вещи
                    </Link>
                    {/* Q18: путь к поиску дублей из разбора, а не только из AI-профиля вручную. */}
                    <Link to={`/clothing/${card.itemId}/ai`} data-testid="declutter-find-duplicates"
                          className="text-brand-600 dark:text-brand-400 hover:underline inline-flex items-center min-h-[44px]">
                        Найти дубли
                    </Link>
                </div>
            </div>

            <div className="card p-4" data-testid="declutter-evidence">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                    <h3 className="text-sm font-medium text-ink-900 dark:text-ink-100">Почему вещь здесь</h3>
                    {bucketLabel(card.priorityBucket) && (
                        <span className="chip">{bucketLabel(card.priorityBucket)}</span>
                    )}
                </div>
                {card.reasons.length > 0 ? (
                    <ul className="mt-2 list-disc pl-5 text-sm text-ink-600 dark:text-ink-300"
                        data-testid="declutter-evidence-reasons">
                        {card.reasons.slice(0, 3).map(r => <li key={r}>{r}</li>)}
                    </ul>
                ) : (
                    <p className="mt-2 text-sm text-ink-500 dark:text-ink-400"
                       data-testid="declutter-evidence-empty">
                        Явных поводов расстаться не нашлось — вещь попала в очередь как часть полного разбора.
                    </p>
                )}
                <details className="mt-3" data-testid="declutter-details">
                <summary className="cursor-pointer min-h-[44px] flex items-center text-sm font-medium
                                    text-ink-900 dark:text-ink-100">
                    Детали расчёта
                </summary>
                <div className="flex items-baseline gap-3 mt-3">
                    <span className="text-3xl font-semibold text-ink-900 dark:text-ink-100"
                          data-testid="declutter-score">
                        {card.score ?? '—'}
                    </span>
                    {/* D04: интегральный балл называется полезностью для гардероба, а не
                        «состоянием» — физическое состояние живёт отдельной строкой. Бейдж
                        причины стоит выше, в блоке доказательства, и здесь не дублируется. */}
                    <span className="text-sm text-ink-500 dark:text-ink-400"
                          data-testid="declutter-score-label">
                        из 100 · {card.scoreLabel ?? 'Полезность для гардероба'}
                    </span>
                </div>
                {visibleFactors.length > 0 && (
                    <ul className="mt-3 divide-y divide-ink-100 dark:divide-ink-700">
                        {visibleFactors.map(f => (
                            <FactorRow key={f.code} label={f.label} impact={f.impact} known={f.known}/>
                        ))}
                    </ul>
                )}
                {card.reasons.length > 3 && (
                    <div className="mt-4">
                        <h4 className="text-sm font-medium text-ink-900 dark:text-ink-100">Остальные причины</h4>
                        <ul className="mt-1 list-disc pl-5 text-sm text-ink-600 dark:text-ink-300">
                            {card.reasons.slice(3).map(r => <li key={r}>{r}</li>)}
                        </ul>
                    </div>
                )}
                </details>
            </div>

            <div className="lg:col-span-2">
                <DecisionRegion card={card} onDecide={onDecide} onDecideCategory={props.onDecideCategory}
                                deciding={deciding} blocked={blocked}/>
            </div>

            {onTryFirst && (
                <div className="lg:col-span-2">
                    <TryFirstPanel onTry={onTryFirst} busy={deciding}/>
                </div>
            )}

            <div className="lg:col-span-2 card p-4">
                <DeclutterObjections ownerId={ownerId} sessionId={sessionId} itemId={card.itemId}/>
            </div>
        </div>
    );
}

/** Сколько строк очереди рисуем за раз: 285 строк сразу — 15 тысяч пикселей на мобиле (Q13). */
const QUEUE_PAGE_SIZE = 30;

/**
 * Очередь разбора (Q13). Решённые строки видно с одного взгляда: чек, приглушённый текст и
 * решение как утверждение; у нерешённых последняя колонка — рекомендация, подписанная как
 * рекомендация. Раньше обе колонки выглядели одинаково, и понять, что уже сделано, было нельзя.
 */
function QueueList({queue, currentItemId, onOpen}: {
    queue: DeclutterQueueItem[];
    currentItemId: string | null;
    onOpen: (itemId: string) => void;
}) {
    const [shown, setShown] = useState(QUEUE_PAGE_SIZE);

    // Смена фильтра категории укорачивает очередь — начинаем показ заново.
    useEffect(() => {
        setShown(QUEUE_PAGE_SIZE);
    }, [queue.length]);

    if (queue.length === 0) return null;
    const rows = queue.slice(0, shown);
    const decidedCount = queue.filter(q => q.decidedAction).length;

    return (
        <div className="card p-4">
            <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                <h3 className="text-sm font-medium text-ink-900 dark:text-ink-100">
                    Очередь разбора
                </h3>
                <span className="text-xs text-ink-500 dark:text-ink-400" data-testid="declutter-queue-summary">
                    Решено {decidedCount} из {queue.length}
                </span>
            </div>
            <ul className="divide-y divide-ink-100 dark:divide-ink-700">
                {rows.map(q => {
                    const decided = actionLabel(q.decidedAction);
                    return (
                        <li key={q.itemId} data-testid="declutter-queue-row"
                            data-decided={decided ? 'true' : 'false'}>
                            <button type="button"
                                    className={`w-full min-h-[44px] py-2 flex items-center gap-3 text-left
                                        rounded-lg focus-visible:ring-2 focus-visible:ring-brand-500 ${
                                        decided ? 'opacity-60' : ''}`}
                                    aria-current={currentItemId === q.itemId ? 'true' : undefined}
                                    onClick={() => onOpen(q.itemId)}>
                                <span className="w-6 text-xs text-ink-400 tabular-nums">{q.rank}</span>
                                <span className={`w-4 shrink-0 text-emerald-600 dark:text-emerald-400 ${
                                    decided ? '' : 'invisible'}`} aria-hidden="true">✓</span>
                                <span className={`flex-1 truncate text-sm ${decided
                                    ? 'text-ink-500 dark:text-ink-400 line-through decoration-ink-300'
                                    : 'text-ink-800 dark:text-ink-200'}`}>
                                    {q.name}
                                </span>
                                <span className="hidden sm:inline chip">{bucketLabel(q.priorityBucket)}</span>
                                <span className="w-10 text-right text-sm tabular-nums">
                                    {q.score ?? '—'}
                                </span>
                                <span className="w-32 text-right text-xs"
                                      data-testid="declutter-queue-action">
                                    {decided
                                        ? <span className="font-medium text-emerald-700 dark:text-emerald-400">
                                            Решено: {decided}
                                        </span>
                                        : <span className="text-ink-500 dark:text-ink-400">
                                            Совет: {actionLabel(q.recommendedAction) ?? '—'}
                                        </span>}
                                </span>
                            </button>
                        </li>
                    );
                })}
            </ul>
            {shown < queue.length && (
                <button type="button" data-testid="declutter-queue-more"
                        className="btn-secondary mt-3 w-full min-h-[44px] rounded-xl text-sm"
                        onClick={() => setShown(n => n + QUEUE_PAGE_SIZE)}>
                    Показать ещё ({queue.length - shown})
                </button>
            )}
        </div>
    );
}

/**
 * Панель категорий (FR-10): цели по категориям и фильтр очереди. Категория без кандидатов
 * показывает цель 0 — это честнее, чем прятать её.
 */
function CategoryPanel({categories, selected, onSelect}: {
    categories: DeclutterCategoryGoal[];
    selected: string | null;
    onSelect: (categoryId: string | null) => void;
}) {
    if (categories.length === 0) return null;
    const chip = (active: boolean) => `min-h-[44px] px-3 rounded-xl text-sm
        focus-visible:ring-2 focus-visible:ring-brand-500 ${active ? 'btn-accent' : 'btn-secondary'}`;
    return (
        <div className="card p-4" data-testid="declutter-categories">
            <h3 className="text-sm font-medium text-ink-900 dark:text-ink-100">По категориям</h3>
            <div className="mt-2 flex flex-wrap gap-2" role="group" aria-label="Фильтр очереди по категории">
                <button type="button" className={chip(selected === null)} aria-pressed={selected === null}
                        data-testid="declutter-category-all" onClick={() => onSelect(null)}>
                    Все
                </button>
                {categories.filter(c => c.categoryId).map(c => (
                    <button key={c.categoryId} type="button" className={chip(selected === c.categoryId)}
                            aria-pressed={selected === c.categoryId}
                            data-testid={`declutter-category-${c.categoryId}`}
                            onClick={() => onSelect(c.categoryId)}>
                        {c.categoryName ?? 'Без категории'}
                        <span className="ml-1 text-xs opacity-70 tabular-nums">
                            {c.decidedForRemoval}/{c.targetCount}
                        </span>
                    </button>
                ))}
            </div>
        </div>
    );
}

/** История разборов: только закрытые сессии — текущая показана выше своей стадией. */
function HistoryList({sessions, currentId}: {sessions: DeclutterSession[]; currentId: string | null}) {
    const closed = sessions.filter(s => s.id !== currentId
        && (s.status === 'COMPLETED' || s.status === 'ABANDONED' || s.status === 'DECISIONS_DONE'));
    if (closed.length === 0) return null;
    return (
        <div className="card p-4" data-testid="declutter-history">
            <h3 className="text-sm font-medium text-ink-900 dark:text-ink-100">История разборов</h3>
            <ul className="mt-2 divide-y divide-ink-100 dark:divide-ink-700">
                {closed.map(s => (
                    <li key={s.id} className="min-h-[44px] py-2 flex items-center gap-3 text-sm"
                        data-testid="declutter-history-row">
                        <span className="flex-1 text-ink-800 dark:text-ink-200">
                            {formatDate(s.startedAt)}
                            {s.quick && ' · быстрый'}
                        </span>
                        <span className="chip">{sessionStatusLabel(s.status)}</span>
                        <span className="w-28 text-right text-xs text-ink-500 tabular-nums">
                            {progressLabel(s.progress.decidedForRemoval, s.progress.targetCount)}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

/**
 * Компактная ссылка на стадию «Ждут передачи» (D08). Физическое завершение — другой этап и
 * другая стадия; на решениях от него нужна ровно одна строка, а не встроенный экран
 * подтверждений. Нуля ожидающих строка не показывает вовсе: пустое напоминание — шум.
 */
function PendingHandoffNotice({count}: {count: number}) {
    if (count <= 0) return null;
    return (
        <Link to="/disposal/pending" data-testid="declutter-pending-notice"
              className="card p-4 flex items-center justify-between gap-3 min-h-[44px]
                         text-sm text-ink-700 dark:text-ink-200 hover:border-brand-500
                         focus-visible:ring-2 focus-visible:ring-brand-500">
            <span>{pluralItems(count)} ждут передачи — отметьте, что с ними произошло</span>
            <span aria-hidden="true" className="text-brand-600 dark:text-brand-400">→</span>
        </Link>
    );
}

/** Задержка пересчёта предпросмотра: перетаскивание ползунка не должно слать запрос на шаг. */
const GRACE_DEBOUNCE_MS = 400;
/** Льгота на случай старого бэкенда, который ещё не отдаёт `defaultNewGraceDays`. */
const GRACE_FALLBACK_DEFAULT = 90;

const DeclutterPage = observer(() => {
    const {selectedOwnerId} = useWardrobe();
    const store = declutterStore;
    const {overview, session, queue, card, report, error, startError, stage} = store;
    const [mode, setMode] = useState('NORMAL');
    // Льгота новизны: null до первой загрузки обзора — начальное положение приходит с сервера.
    const [graceDays, setGraceDays] = useState<number | null>(null);
    const [recalculating, setRecalculating] = useState(false);
    // Предпросмотр под ползунок живёт отдельно от обзора стора: он не меняет стадию страницы.
    const [preview, setPreview] = useState<{candidateCount: number; quickAvailableCount: number} | null>(null);
    const [refreshingScore, setRefreshingScore] = useState(false);
    // D03: явное «решаю по текущим данным» — по одной вещи, не на весь разбор.
    const [acceptOutdatedFor, setAcceptOutdatedFor] = useState<string | null>(null);
    const cardRef = useRef<HTMLDivElement>(null);
    // Прокрутка нужна только после решения: при первой отрисовке страницы дёргать экран незачем.
    const scrollToCard = useRef(false);
    const {showToast} = useToast();

    useEffect(() => {
        setGraceDays(null);
        setPreview(null);
        void store.load(selectedOwnerId);
    }, [store, selectedOwnerId]);

    useEffect(() => {
        if (overview && graceDays == null) {
            setGraceDays(overview.defaultNewGraceDays ?? GRACE_FALLBACK_DEFAULT);
        }
    }, [overview, graceDays]);

    // Пересчёт предпросмотра под ползунок: с задержкой, чтобы перетаскивание не слало запрос на
    // каждый шаг, и с проверкой актуальности — ответ по устаревшему значению применять нельзя.
    useEffect(() => {
        if (graceDays == null || overview == null || overview.newGraceDays === graceDays) {
            setPreview(null);
            return undefined;
        }
        let stale = false;
        setRecalculating(true);
        const timer = setTimeout(() => {
            fetchOverview(selectedOwnerId, graceDays)
                .then(data => {
                    if (!stale) {
                        setPreview({candidateCount: data.candidateCount,
                            quickAvailableCount: data.quickAvailableCount});
                    }
                })
                .catch(() => undefined)
                .finally(() => {
                    if (!stale) {
                        setRecalculating(false);
                    }
                });
        }, GRACE_DEBOUNCE_MS);
        return () => {
            stale = true;
            clearTimeout(timer);
        };
        // overview.newGraceDays — единственное, что нужно от обзора: полный объект в зависимостях
        // зациклил бы эффект.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [graceDays, overview?.newGraceDays, selectedOwnerId]);

    /**
     * Q13: после решения подставляется следующая вещь, а экран оставался на разложении
     * предыдущей — имя и фото новой были за кадром. Ведём к началу карточки; при
     * `prefers-reduced-motion` — без плавности, мгновенным переходом.
     */
    useEffect(() => {
        if (!scrollToCard.current || !card) return;
        scrollToCard.current = false;
        const node = cardRef.current;
        if (!node?.scrollIntoView) return;
        const reduced = typeof window !== 'undefined' && window.matchMedia
            && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        node.scrollIntoView({behavior: reduced ? 'auto' : 'smooth', block: 'start'});
    }, [card]);

    /**
     * Решение с правом на ошибку (Q13): тост называет вещь и решение и даёт «Отменить»,
     * которое снимает решение тем же эндпоинтом, что и кнопка отмены в очереди.
     */
    /** Категория вещи по имени: карточка несёт только название, список целей — идентификатор. */
    const categoryIdOf = useCallback((name: string | null): string | null => {
        const goals = session && session.categories.length > 0
            ? session.categories : overview?.categories ?? [];
        return goals.find(c => c.categoryName === name)?.categoryId ?? null;
    }, [session, overview]);

    /** Одно решение на всю категорию текущей вещи (правка владельца 17.09). */
    const decideCategory = useCallback(async (action: DeclutterAction) => {
        const current = store.card;
        if (!current) return;
        const categoryId = categoryIdOf(current.categoryName);
        if (!categoryId) {
            showToast('Категория вещи не определена — решение по категории недоступно', 'error');
            return;
        }
        scrollToCard.current = true;
        const done = await store.decideCategory(action, categoryId);
        if (store.error) {
            scrollToCard.current = false;
            showToast(store.error, 'error');
            return;
        }
        showToast(`Решение «${actionLabel(action)}» применено: ${pluralItems(done)} в категории «${current.categoryName}»`,
            'success');
    }, [categoryIdOf, showToast]);

    const decide = useCallback(async (action: DeclutterAction) => {
        const decided = store.card;
        if (!decided) return;
        // Флаг ставим до запроса: подстановка следующей карточки и перерисовка происходят
        // сразу после ответа, и эффект прокрутки не должен их опередить.
        scrollToCard.current = true;
        await store.decide(action, acceptOutdatedFor === decided.itemId);
        if (store.error) {
            scrollToCard.current = false;
            return;
        }
        showToast(`Решение: ${actionLabel(action)} — ${decided.name}`, 'success', {
            label: 'Отменить',
            onClick: () => {
                void store.undoDecision(decided.itemId).then(ok => {
                    showToast(ok ? 'Решение отменено' : 'Не удалось отменить решение',
                        ok ? 'info' : 'error');
                });
            },
        });
    }, [store, showToast, acceptOutdatedFor]);

    /**
     * «Обновить оценку» (аудит 17.09, D03): не перезапрос карточки, а настоящий пересчёт.
     * Ответ несёт время расчёта и дифф «было → стало», а сбой виден пользователю.
     */
    const refreshScore = useCallback(async () => {
        const current = store.card;
        if (!current) return;
        setRefreshingScore(true);
        try {
            await store.reassess();
        } finally {
            setRefreshingScore(false);
        }
    }, [store]);

    /** «Сначала попробовать» (D10): вещь уходит на восстановление, решение переносится. */
    const tryFirst = useCallback(async (kind: RestorationKind, dueDate: string | null) => {
        const current = store.card;
        if (!current) return;
        scrollToCard.current = true;
        const ok = await store.tryFirst(kind, dueDate);
        if (!ok) {
            scrollToCard.current = false;
            return;
        }
        showToast(`«${current.name}» отправлена на восстановление`, 'success');
    }, [store, showToast]);

    const onStart = (quick: boolean) => {
        void store.start(mode, quick, graceDays ?? undefined);
    };

    if (store.loading) {
        return <div className="flex justify-center py-16"><Spinner/></div>;
    }

    if (error && !overview) {
        return (
            <div className="text-center py-16">
                <p className="text-ink-600 dark:text-ink-300">{error}</p>
                <button type="button" className="btn-secondary mt-3 min-h-[44px] px-4 rounded-xl"
                        onClick={() => void store.load(selectedOwnerId)}>
                    Повторить
                </button>
            </div>
        );
    }

    const candidateCount = preview?.candidateCount ?? overview?.candidateCount ?? 0;
    const quickAvailableCount = preview?.quickAvailableCount ?? overview?.quickAvailableCount ?? 0;
    const startBusy = store.starting;

    return (
        <div className="space-y-4" data-testid={`declutter-stage-${stage}`}>
            {error && (
                <p className="text-sm text-red-600 dark:text-red-400" role="alert">{error}</p>
            )}

            {overview && stage === 'overview' && (
                <>
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                        <div className="stat-card" data-testid="declutter-active">
                            <div className="text-2xl font-semibold">{overview.activeCount}</div>
                            <div className="text-sm text-ink-500 dark:text-ink-400">
                                Вещей в разборе
                            </div>
                        </div>
                        <div className="stat-card" data-testid="declutter-candidates">
                            <div className="text-2xl font-semibold">{candidateCount}</div>
                            <div className="text-sm text-ink-500 dark:text-ink-400">
                                С поводом присмотреться
                            </div>
                        </div>
                        <div className="stat-card" data-testid="declutter-duplicates"
                            title={overview.duplicatesNotComputed
                                ? `Без индекса: ${pluralItems(overview.duplicatesUnindexedCount ?? 0)} — дубли посчитаются после индексации`
                                : undefined}>
                            <div className="text-2xl font-semibold">
                                {overview.duplicatesNotComputed ? '—' : overview.duplicateGroupCount}
                            </div>
                            <div className="text-sm text-ink-500 dark:text-ink-400">
                                {overview.duplicatesNotComputed ? 'Дубли не посчитаны' : 'Дублей'}
                            </div>
                        </div>
                        <div className="stat-card">
                            <div className="text-2xl font-semibold">{overview.notWornOverYearCount}</div>
                            <div className="text-sm text-ink-500 dark:text-ink-400">Не носили &gt; года</div>
                        </div>
                    </div>

                    {overview.problemZones.length > 0 && (
                        <div className="card p-4">
                            <h3 className="text-sm font-medium text-ink-900 dark:text-ink-100">
                                На что посмотреть
                            </h3>
                            <ul className="mt-2 list-disc pl-5 text-sm text-ink-600 dark:text-ink-300">
                                {overview.problemZones.map(z => <li key={z}>{z}</li>)}
                            </ul>
                        </div>
                    )}

                    <div className="card p-4">
                        <label className="block text-sm font-medium text-ink-900 dark:text-ink-100"
                               htmlFor="declutter-mode">
                            Режим разбора
                        </label>
                        <select id="declutter-mode" className="input-field mt-1 max-w-xs min-h-[44px]"
                                value={mode} onChange={e => setMode(e.target.value)}>
                            {MODE_OPTIONS.map(o => (
                                <option key={o.value} value={o.value}>{o.label} — {o.hint}</option>
                            ))}
                        </select>
                        <GracePeriodSlider
                            value={graceDays ?? overview.defaultNewGraceDays ?? GRACE_FALLBACK_DEFAULT}
                            max={overview.maxNewGraceDays ?? GRACE_FALLBACK_MAX}
                            flaggedCount={candidateCount}
                            quickCount={quickAvailableCount}
                            recalculating={recalculating}
                            onChange={setGraceDays}
                        />

                        <div className="mt-3 flex flex-wrap gap-2">
                            <button type="button" className="btn-accent min-h-[44px] px-4 rounded-xl"
                                    data-testid="declutter-start"
                                    disabled={overview.activeCount === 0 || startBusy}
                                    aria-busy={startBusy}
                                    onClick={() => onStart(false)}>
                                {startBusy ? 'Начинаем…' : 'Начать разбор'}
                            </button>
                            <button type="button" className="btn-secondary min-h-[44px] px-4 rounded-xl"
                                    data-testid="declutter-start-quick"
                                    disabled={quickAvailableCount === 0 || startBusy}
                                    aria-busy={startBusy}
                                    onClick={() => onStart(true)}>
                                Быстрый разбор
                            </button>
                        </div>
                        {startError && (
                            <div className="mt-2 flex flex-wrap items-center gap-2" role="alert"
                                 data-testid="declutter-start-error">
                                <span className="text-sm text-red-600 dark:text-red-400">
                                    {startError.message}
                                </span>
                                {startError.code === 'SESSION_ALREADY_OPEN' && (
                                    <button type="button"
                                            className="btn-secondary min-h-[44px] px-4 rounded-xl"
                                            data-testid="declutter-start-continue"
                                            onClick={() => void store.load(selectedOwnerId)}>
                                        Продолжить разбор
                                    </button>
                                )}
                            </div>
                        )}
                        <p className="mt-2 text-sm text-ink-500 dark:text-ink-400"
                           data-testid="declutter-start-hint">
                            Полный разбор ставит под сомнение все вещи гардероба
                            ({overview.activeCount}) — решение по каждой принимаете вы.
                            Быстрый — короткая очередь из тех, где система нашла повод
                            ({quickAvailableCount}).
                        </p>
                        {overview.activeCount === 0 && (
                            <p className="mt-2 text-sm text-ink-500 dark:text-ink-400"
                               data-testid="declutter-empty">
                                В гардеробе нет вещей — разбирать нечего.
                            </p>
                        )}
                    </div>

                    <HistoryList sessions={store.history} currentId={null}/>
                </>
            )}

            {session && stage === 'decisions' && (
                <>
                    <div className="card p-4">
                        <ProgressBar progress={session.progress}/>
                        <div className="mt-3 flex flex-wrap gap-2">
                            <button type="button" className="btn-secondary min-h-[44px] px-4 rounded-xl"
                                    data-testid="declutter-complete" disabled={store.completing}
                                    aria-busy={store.completing}
                                    onClick={() => void store.complete()}>
                                Завершить решения
                            </button>
                            <span className="self-center text-xs text-ink-500 dark:text-ink-400">
                                Вещи остаются у вас: передача — отдельная стадия
                            </span>
                        </div>
                    </div>

                    <CategoryPanel categories={session.categories.length > 0
                        ? session.categories : overview?.categories ?? []}
                                   selected={store.categoryId}
                                   onSelect={id => void store.setCategory(id)}/>

                    {card
                        ? <ItemCardView card={card} onDecide={a => void decide(a)}
                                        onDecideCategory={a => void decideCategory(a)}
                                        deciding={store.deciding}
                                        ownerId={selectedOwnerId} sessionId={session.id}
                                        cardRef={cardRef}
                                        onRefreshScore={() => void refreshScore()}
                                        refreshingScore={refreshingScore}
                                        acceptOutdated={acceptOutdatedFor === card.itemId}
                                        onAcceptOutdated={() => setAcceptOutdatedFor(card.itemId)}
                                        reassessment={store.reassessment}
                                        reassessError={store.reassessError}
                                        onTryFirst={(k, d) => void tryFirst(k, d)}/>
                        : (
                            <div className="card p-6 text-center" data-testid="declutter-queue-done">
                                <p className="text-ink-700 dark:text-ink-200">
                                    {store.categoryId
                                        ? 'В этой категории решения приняты по всем вещам.'
                                        : 'Очередь разобрана — решения приняты по всем вещам.'}
                                </p>
                            </div>
                        )}

                    <DeclutterDuelCard ownerId={selectedOwnerId} sessionId={session.id}
                                       generate={(_, id) => store.generateDuelsOnce(id)}
                                       onResolved={() => void store.refresh()}/>

                    {/* D08: физическое завершение живёт в своей стадии. Здесь от него остаётся
                        одна строка — иначе человек, решающий по первой из десяти вещей, посреди
                        разбора проваливался в подтверждение старых выбытий. */}
                    <PendingHandoffNotice count={store.pending?.items.length ?? 0}/>

                    <QueueList queue={queue} currentItemId={card?.itemId ?? null}
                               onOpen={id => void store.openItem(id)}/>
                </>
            )}

            {stage === 'aftercare' && (
                <>
                    {report && <DeclutterReportCard report={report}/>}

                    <DeclutterPhysicalStep ownerId={selectedOwnerId} data={store.pending}
                                           onChanged={() => void store.refresh()}/>

                    <SaleListings ownerId={selectedOwnerId} listings={store.listings}
                                  marketplaces={store.marketplaces}
                                  onChanged={() => void store.refresh()}/>

                    <div className="flex flex-wrap items-center gap-2">
                        <button type="button" className="btn-secondary min-h-[44px] px-4 rounded-xl"
                                data-testid="declutter-new-session"
                                onClick={store.openStartPanel}>
                            Начать новый разбор
                        </button>
                        <span className="text-xs text-ink-500 dark:text-ink-400">
                            Пакет у шкафа можно доводить до конца и параллельно с новым разбором.
                        </span>
                    </div>

                    <HistoryList sessions={store.history} currentId={session?.id ?? null}/>
                </>
            )}
        </div>
    );
});

export default DeclutterPage;
